<template>
  <div class="chart-container">
    <canvas 
      :ref="chartRef" 
      :width="width" 
      :height="height"
      :class="chartClass"
    ></canvas>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js'
import 'chartjs-adapter-date-fns'

// Register Chart.js components
Chart.register(...registerables)

export default {
  name: 'BaseChart',
  // Static chart initialization queue to prevent conflicts
  static: {
    chartInitQueue: [],
    isInitializing: false
  },
  props: {
    // Chart configuration
    chartType: {
      type: String,
      default: 'line',
      validator: (value) => ['line', 'bar', 'scatter', 'radar', 'doughnut', 'pie'].includes(value)
    },
    chartData: {
      type: Object,
      required: true
    },
    chartOptions: {
      type: Object,
      default: () => ({})
    },
    chartPlugins: {
      type: Array,
      default: () => []
    },
    // Canvas properties
    width: {
      type: [Number, String],
      default: 400
    },
    height: {
      type: [Number, String],
      default: 300
    },
    chartClass: {
      type: String,
      default: ''
    },
    // Chart lifecycle
    autoUpdate: {
      type: Boolean,
      default: true
    },
    responsive: {
      type: Boolean,
      default: true
    },
    maintainAspectRatio: {
      type: Boolean,
      default: false
    },
    chartRef: {
      type: String,
      default: 'chartCanvas'
    }
  },
  data() {
    return {
      chart: null,
      isDestroyed: false,
      _updatingData: false,
      _updatingOptions: false,
      _cachedMergedOptions: null,
      _errorState: false,
      _lastErrorTime: 0,
      _initPromise: null,
      debouncedUpdate: null
    }
  },
  computed: {
    // Default options that can be overridden
    defaultOptions() {
      return {
        responsive: this.responsive,
        maintainAspectRatio: this.maintainAspectRatio,
        animation: {
          duration: 0,
          delay: 0
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        },
        plugins: {
          tooltip: {
            enabled: false
          }
        }
      }
    },
    
    // Merged options with defaults
    mergedOptions() {
      // Only merge if chartOptions exists and is different from last merge
      if (!this.chartOptions) {
        return this.defaultOptions
      }
      
      // Cache the merged options to prevent unnecessary recalculations
      if (!this._cachedMergedOptions || this._cachedMergedOptions.source !== this.chartOptions) {
        this._cachedMergedOptions = {
          source: this.chartOptions,
          result: this.createNonReactiveOptions(this.deepMerge(this.defaultOptions, this.chartOptions))
        }
      }
      
      return this._cachedMergedOptions.result
    }
  },
  // Watchers removed to prevent infinite loops - use explicit update methods instead
  beforeUnmount() {
    // Reset update flags and cache
    this._updatingData = false
    this._updatingOptions = false
    this._cachedMergedOptions = null
    
    // Clean up any pending initialization
    if (this._initPromise) {
      console.log('📊 BaseChart: Cleaning up pending initialization')
      this._initPromise = null
    }
    
    // Clean up debounced update
    if (this.debouncedUpdate) {
      clearTimeout(this.debouncedUpdate)
      this.debouncedUpdate = null
    }
  },
  methods: {
    // Deep merge utility
    deepMerge(target, source) {
      const result = { ...target }
      
      for (const key in source) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
          result[key] = this.deepMerge(result[key] || {}, source[key])
        } else {
          result[key] = source[key]
        }
      }
      
      return result
    },
    
    // Create a deep copy of chart data to break reactive references
    createChartDataCopy(data) {
      if (!data || typeof data !== 'object') {
        return data
      }
      
      try {
        // Use JSON.parse(JSON.stringify()) to create a completely non-reactive deep copy
        // This breaks ALL Vue reactivity and prevents infinite loops
        const copy = JSON.parse(JSON.stringify(data))
        console.log('📊 BaseChart: Created non-reactive chart data copy')
        return copy
      } catch (error) {
        console.warn('📊 BaseChart: Error creating chart data copy, using original:', error)
        return data
      }
    },
    
    // Initialize the chart with queue system to prevent conflicts
    async initializeChart() {

      
      // If already initializing, wait for current operation to complete
      if (this._initPromise) {
        console.log('📊 BaseChart: Already initializing, waiting for completion...')
        return await this._initPromise
      }
      
      // Create a promise for this initialization
      this._initPromise = this._performChartInitialization()
      
      try {
        const result = await this._initPromise
        return result
      } finally {
        this._initPromise = null
      }
    },
    
    // Perform the actual chart initialization with queue system
    async _performChartInitialization() {
      // Add this chart to the queue
      const queueId = Date.now() + Math.random()
      this.$options.static.chartInitQueue.push(queueId)
      
      // Wait for our turn in the queue
      while (this.$options.static.chartInitQueue[0] !== queueId) {
        await new Promise(resolve => setTimeout(resolve, 50)) // Wait 50ms before checking again
      }
      
      // Mark as initializing
      this.$options.static.isInitializing = true
      
      try {
        // Clean up any existing chart
        await this.destroyChart()
        
        // Get canvas element
        const canvas = this.$refs[this.chartRef]
        if (!canvas) {
          console.error('📊 BaseChart: Canvas element not found')
          return false
        }
        
        // Check if canvas is already in use by another chart instance
        if (canvas.chart && canvas.chart !== this.chart) {
          console.log('📊 BaseChart: Canvas already has chart, destroying it first')
          try {
            canvas.chart.destroy()
            canvas.chart = null
          } catch (error) {
            console.warn('📊 BaseChart: Error destroying existing chart on canvas:', error)
            // Force cleanup
            canvas.chart = null
          }
        }
        
        // Get 2D context
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          console.error('📊 BaseChart: 2D context not available')
          return false
        }
        
        // Validate chart data
        if (!this.validateChartData(this.chartData)) {
          console.error('📊 BaseChart: Invalid chart data')
          return false
        }
        
        // Only create chart if we have actual data
        if (!this.chartData || !this.chartData.datasets || this.chartData.datasets.length === 0) {
          return false
        }
        
        // Create new chart instance with non-reactive options and data copy
        this.chart = new Chart(ctx, {
          type: this.chartType,
          data: this.createChartDataCopy(this.chartData),
          options: this.createNonReactiveOptions(this.mergedOptions),
          plugins: Array.isArray(this.chartPlugins) ? this.chartPlugins : []
        })
        
        // Store reference to chart on canvas for cleanup
        canvas.chart = this.chart
        
        this.isDestroyed = false
        
        // Emit ready event
        this.$emit('chart-ready', this.chart)
        
        return true
      } catch (error) {
        console.error('📊 BaseChart: Error initializing chart:', error)
        this.$emit('chart-error', error)
        return false
      } finally {
        // Remove from queue and mark as not initializing
        const index = this.$options.static.chartInitQueue.indexOf(queueId)
        if (index > -1) {
          this.$options.static.chartInitQueue.splice(index, 1)
        }
        this.$options.static.isInitializing = false
      }
    },
    
    // Validate chart data structure
    validateChartData(data) {

      
      if (!data || typeof data !== 'object') {
        console.log('📊 BaseChart: validateChartData failed - no data or not object')
        return false
      }
      
      if (!Array.isArray(data.datasets)) {
        console.log('📊 BaseChart: validateChartData failed - no datasets array')
        return false
      }
      
      // Check if datasets have required properties
      for (const dataset of data.datasets) {
        if (!dataset.label || !Array.isArray(dataset.data)) {
          console.log('📊 BaseChart: validateChartData failed - invalid dataset:', {
            hasLabel: !!dataset.label,
            hasData: !!dataset.data,
            isDataArray: Array.isArray(dataset.data)
          })
          return false
        }
      }
      
      return true
    },
    
    // Update chart data
    async updateChartData(newData) {
      console.log('📊 BaseChart: updateChartData called with:', {
        hasChart: !!this.chart,
        isDestroyed: this.isDestroyed,
        newDataKeys: newData ? Object.keys(newData) : [],
        datasetsCount: newData?.datasets?.length || 0
      })
      
      if (!this.chart || this.isDestroyed) {
        console.log('📊 BaseChart: updateChartData blocked - no chart or destroyed')
        return
      }
      
      // Simple check to prevent basic recursion
      if (this._updatingData) {
        console.log('🔄 updateChartData already in progress, skipping')
        return
      }
      
      // Check if we're in error state and should block updates
      if (this._errorState && Date.now() - this._lastErrorTime < 5000) {
        console.log('🚫 updateChartData blocked - in error state')
        return
      }
      
      this._updatingData = true
      
      try {
        // Validate new data
        if (!this.validateChartData(newData)) {
          console.warn('📊 BaseChart: Invalid chart data, skipping update')
          return
        }
        
        // Prevent updating with the same data reference to avoid infinite loops
        if (this.chart.data === newData) {
          console.log('📊 BaseChart: Same data reference, skipping update')
          return
        }
        
        console.log('📊 BaseChart: Updating chart with new data, datasets:', newData.datasets?.length || 0)
        
        // Log the actual data being sent to Chart.js to confirm it's changing
        if (newData.datasets && newData.datasets.length > 0) {
          const firstDataset = newData.datasets[0]
          if (firstDataset.data && firstDataset.data.length > 0) {
            const firstPoint = firstDataset.data[0]
            const lastPoint = firstDataset.data[firstDataset.data.length - 1]
            console.log('📊 BaseChart: Chart data range:', {
              datasetLabel: firstDataset.label,
              firstPoint: firstPoint,
              lastPoint: lastPoint,
              totalPoints: firstDataset.data.length
            })
          }
        }
        
        // Update chart data with a copy to break reactive references
        this.chart.data = this.createChartDataCopy(newData)
        console.log('📊 BaseChart: Chart data set successfully')
        
        // Don't call chart.update() here - it causes stack overflow due to Vue reactivity
        // Chart updates will be handled by the parent component when dragging stops
        console.log('📊 BaseChart: Chart data updated successfully (no manual update)')
        this._errorState = false // Reset error state on successful update
        this.$emit('chart-updated', this.chart)
              } catch (error) {
          console.error('📊 BaseChart: Error updating chart data:', error)
          this._errorState = true
          this._lastErrorTime = Date.now()
          this.$emit('chart-error', error)
        } finally {
          this._updatingData = false
        }
    },
    
    // Update chart options
    updateChartOptions(newOptions) {
      if (!this.chart || this.isDestroyed) return
      
      // Simple check to prevent basic recursion
      if (this._updatingOptions) {
        console.log('🔄 updateChartOptions already in progress, skipping')
        return
      }
      
      // Check if we're in error state and should block updates
      if (this._errorState && Date.now() - this._lastErrorTime < 5000) {
        console.log('🚫 updateChartOptions blocked - in error state')
        return
      }
      
      this._updatingOptions = true
      
      try {
        // Create completely non-reactive chart options using Object.create(null)
        const isolatedOptions = this.createNonReactiveOptions(newOptions)
        
        console.log('📊 BaseChart: Updating chart options with non-reactive copy')
        // Update chart options
        this.chart.options = isolatedOptions
        console.log('📊 BaseChart: Chart options set successfully')
        
        // Don't call chart.update() here - it causes stack overflow due to Vue reactivity
        // Chart updates will be handled by the parent component when dragging stops
        console.log('📊 BaseChart: Chart options updated successfully (no manual update)')
        this._errorState = false // Reset error state on successful update
        this.$emit('chart-updated', this.chart)
              } catch (error) {
          console.error('Error updating chart options:', error)
          this._errorState = true
          this._lastErrorTime = Date.now()
          this.$emit('chart-error', error)
        } finally {
          this._updatingOptions = false
        }
    },
    

    
    // NEW: Public method for DataExplorer to call - updates chart data
    async updateChartData(newData) {
      console.log('📊 BaseChart: Public updateChartData called with:', {
        hasChart: !!this.chart,
        isDestroyed: this.isDestroyed,
        newDataKeys: newData ? Object.keys(newData) : [],
        datasetsCount: newData?.datasets?.length || 0
      })
      
      if (!this.chart || this.isDestroyed) {
        console.log('📊 BaseChart: updateChartData blocked - no chart or destroyed')
        return false
      }
      
      // Simple check to prevent basic recursion
      if (this._updatingData) {
        console.log('🔄 updateChartData already in progress, skipping')
        return false
      }
      
      this._updatingData = true
      
      try {
        // Validate new data
        if (!this.validateChartData(newData)) {
          console.warn('📊 BaseChart: Invalid chart data, skipping update')
          return false
        }
        
        console.log('📊 BaseChart: Updating chart with new data, datasets:', newData.datasets?.length || 0)
        
        // Log the actual data being sent to Chart.js to confirm it's changing
        if (newData.datasets && newData.datasets.length > 0) {
          const firstDataset = newData.datasets[0]
          if (firstDataset.data && firstDataset.data.length > 0) {
            const firstPoint = firstDataset.data[0]
            const lastPoint = firstDataset.data[firstDataset.data.length - 1]
            console.log('📊 BaseChart: Chart data range:', {
              datasetLabel: firstDataset.label,
              firstPoint: firstPoint,
              lastPoint: lastPoint,
              totalPoints: firstDataset.data.length
            })
          }
        }
        
        // Update chart data with a copy to break reactive references
        this.chart.data = this.createChartDataCopy(newData)
        console.log('📊 BaseChart: Chart data set successfully')
        
        // Don't call chart.update() here - it causes stack overflow due to Vue reactivity
        // Chart updates will be handled by the parent component when dragging stops
        console.log('📊 BaseChart: Chart data updated successfully (no manual update)')
        this._errorState = false // Reset error state on successful update
        this.$emit('chart-updated', this.chart)
        return true
      } catch (error) {
        console.error('📊 BaseChart: Error updating chart data:', error)
        this._errorState = true
        this._lastErrorTime = Date.now()
        this.$emit('chart-error', error)
        return false
      } finally {
        this._updatingData = false
      }
    },
    
    // NEW: Public method for DataExplorer to call - updates chart options
    updateChartOptions(newOptions) {
      console.log('📊 BaseChart: Public updateChartOptions called with:', {
        hasChart: !!this.chart,
        isDestroyed: this.isDestroyed,
        newOptionsKeys: newOptions ? Object.keys(newOptions) : []
      })
      
      if (!this.chart || this.isDestroyed) {
        console.log('📊 BaseChart: updateChartOptions blocked - no chart or destroyed')
        return false
      }
      
      // Simple check to prevent basic recursion
      if (this._updatingOptions) {
        console.log('🔄 updateChartOptions already in progress, skipping')
        return false
      }
      
      this._updatingOptions = true
      
      try {
        // Create completely non-reactive chart options using Object.create(null)
        const isolatedOptions = this.createNonReactiveOptions(newOptions)
        
        console.log('📊 BaseChart: Updating chart options with non-reactive copy')
        // Update chart options
        this.chart.options = isolatedOptions
        console.log('📊 BaseChart: Chart options set successfully')
        
        // Don't call chart.update() here - it causes stack overflow due to Vue reactivity
        // Chart updates will be handled by the parent component when dragging stops
        console.log('📊 BaseChart: Chart options updated successfully (no manual update)')
        this._errorState = false // Reset error state on successful update
        this.$emit('chart-updated', this.chart)
        return true
      } catch (error) {
        console.error('📊 BaseChart: Error updating chart options:', error)
        this._errorState = true
        this._lastErrorTime = Date.now()
        this.$emit('chart-error', error)
        return false
      } finally {
        this._updatingOptions = false
      }
    },
    
    // NEW: Public method for DataExplorer to call - updates both data and options
    async updateChart(newData = null, newOptions = null) {
      console.log('📊 BaseChart: Public updateChart called with:', {
        hasNewData: !!newData,
        hasNewOptions: !!newOptions,
        hasChart: !!this.chart,
        isDestroyed: this.isDestroyed
      })
      
      // If called with nulls, treat as force re-render
      if (!newData && !newOptions) {
        try {
          await this.destroyChart()
          await this.initializeChart()
          return true
        } catch (e) {
          return false
        }
      }
      if (!this.chart || this.isDestroyed) {
        console.log('📊 BaseChart: updateChart blocked - no chart or destroyed')
        return false
      }
      
      let success = true
      
      if (newData) {
        success = success && this.updateChartData(newData)
      } else {
        // If no new data provided, force a chart re-render to update with current data
        console.log('📊 BaseChart: No new data provided, forcing chart re-render')
        try {
          // Nuclear option: destroy and recreate the chart to ensure it updates
          console.log('📊 BaseChart: Destroying and recreating chart to force update')
          await this.destroyChart()
          await this.initializeChart()
          console.log('📊 BaseChart: Chart destroyed and recreated successfully')
        } catch (error) {
          console.error('📊 BaseChart: Error destroying/recreating chart:', error)
          success = false
        }
      }
      
      if (newOptions) {
        success = success && this.updateChartOptions(newOptions)
      }
      
      return success
    },
    
    // Update chart with new data and options in a single operation
    async updateChartDataAndOptions(newData = null, newOptions = null) {
      if (!this.chart || this.isDestroyed) return
      
      // Simple check to prevent basic recursion
      if (this._updatingData || this._updatingOptions) {
        console.log('🔄 Chart update already in progress, skipping')
        return
      }
      
      // Check if we're in error state and should block updates
      if (this._errorState && Date.now() - this._lastErrorTime < 5000) {
        console.log('🚫 Chart update blocked - in error state')
        return
      }
      
      this._updatingData = true
      this._updatingOptions = true
      
      try {
        // Update data if provided
        if (newData) {
          if (!this.validateChartData(newData)) {
            console.warn('📊 BaseChart: Invalid chart data, skipping update')
            return
          }
          
          // Prevent updating with the same data reference to avoid infinite loops
          if (this.chart.data === newData) {
            console.log('📊 BaseChart: Same data reference, skipping update')
            return
          }
          
          console.log('📊 BaseChart: Updating chart data, datasets:', newData.datasets?.length || 0)
          this.chart.data = this.createChartDataCopy(newData)
        }
        
        // Update options if provided
        if (newOptions) {
          const isolatedOptions = this.createNonReactiveOptions(newOptions)
          this.chart.options = isolatedOptions
        }
        
        // Update the chart
        console.log('📊 BaseChart: Chart data and options set successfully')
        
        // Don't call chart.update() here - it causes stack overflow due to Vue reactivity
        // Chart updates will be handled by the parent component when dragging stops
        console.log('📊 BaseChart: Chart updated successfully (no manual update)')
        this._errorState = false // Reset error state on successful update
        this.$emit('chart-updated', this.chart)
      } catch (error) {
        console.error('📊 BaseChart: Error updating chart:', error)
        this._errorState = true
        this._lastErrorTime = Date.now()
        this.$emit('chart-error', error)
      } finally {
        this._updatingData = false
        this._updatingOptions = false
      }
    },
    
    // Debounced chart update to prevent rapid updates
    debouncedUpdateChart(newData = null, newOptions = null, delay = 100) {
      // Clear any existing timeout
      if (this.debouncedUpdate) {
        clearTimeout(this.debouncedUpdate)
      }
      
      // Set new timeout
      this.debouncedUpdate = setTimeout(() => {
        this.updateChartDataAndOptions(newData, newOptions)
        this.debouncedUpdate = null
      }, delay)
    },
    
    // Destroy chart safely
    async destroyChart() {
      // Clear any pending timeouts
      if (this.debouncedUpdate) {
        clearTimeout(this.debouncedUpdate)
        this.debouncedUpdate = null
      }
      
      // Reset update flags and cache
      this._updatingData = false
      this._updatingOptions = false
      this._cachedMergedOptions = null
      
      if (!this.chart) return
      
      try {
        // Stop any ongoing operations
        if (this.chart.stop) {
          this.chart.stop()
        }
        
        // Disable animations and responsive updates
        if (this.chart.options) {
          if (this.chart.options.animation) {
            this.chart.options.animation.duration = 0
            this.chart.options.animation.delay = 0
          }
          if (this.chart.options.responsive) {
            this.chart.options.responsive = false
          }
        }
        
        // Destroy the chart
        this.chart.destroy()
        console.log('📊 BaseChart: Chart destroyed successfully')
        
        // Clear canvas reference
        const canvas = this.$refs[this.chartRef]
        if (canvas && canvas.chart === this.chart) {
          canvas.chart = null
        }
        
      } catch (error) {
        console.warn('📊 BaseChart: Error destroying chart:', error)
        
        // Force cleanup even if destroy fails
        try {
          // Clear canvas reference
          const canvas = this.$refs[this.chartRef]
          if (canvas && canvas.chart === this.chart) {
            canvas.chart = null
          }
        } catch (cleanupError) {
          console.warn('📊 BaseChart: Error during canvas cleanup:', cleanupError)
        }
      } finally {
        // Always clear the chart reference
        this.chart = null
        this.isDestroyed = true
      }
    },
    
    // Force stop chart operations
    forceStopChart() {
      if (!this.chart || this.isDestroyed) return
      
      try {
        // Stop the chart's animation loop
        if (this.chart.stop) {
          this.chart.stop()
        }
        
        // Disable animations
        if (this.chart.options && this.chart.options.animation) {
          this.chart.options.animation.duration = 0
          this.chart.options.animation.delay = 0
        }
        
        // Disable responsive updates
        if (this.chart.options && this.chart.options.responsive) {
          this.chart.options.responsive = false
        }
        
        // Force a final render to clear any pending operations
        if (this.chart.ctx) {
          this.chart.ctx.clearRect(0, 0, this.chart.ctx.canvas.width, this.chart.ctx.canvas.height)
        }
        
        console.log('Chart operations force stopped')
      } catch (error) {
        console.warn('Error force stopping chart:', error)
      }
    },
    
    // Force stop all chart operations and clear queue
    forceStopAll() {
      // Force stop current chart
      this.forceStopChart()
      
      // Clear the queue
      this.clearChartQueue()
      
      // Clear debounced update
      if (this.debouncedUpdate) {
        clearTimeout(this.debouncedUpdate)
        this.debouncedUpdate = null
      }
      
      // Reset all flags
      this._updatingData = false
      this._updatingOptions = false
      this._errorState = false
      this._lastErrorTime = 0
      
      console.log('📊 BaseChart: All operations force stopped and queue cleared')
    },
    
    // Check if chart is healthy
    isChartHealthy() {
      if (!this.chart || this.isDestroyed) {
        return false
      }
      
      try {
        // Basic validation
        if (!this.chart.ctx || !this.chart.ctx.canvas) {
          return false
        }
        
        // Test if context can perform basic operations
        try {
          this.chart.ctx.save()
          this.chart.ctx.restore()
        } catch (ctxError) {
          return false
        }
        
        // Check canvas dimensions
        if (this.chart.ctx.canvas.width <= 0 || this.chart.ctx.canvas.height <= 0) {
          return false
        }
        
        // Check if chart is destroyed
        if (this.chart.destroyed) {
          return false
        }
        
        return true
      } catch (error) {
        return false
      }
    },
    
    // Get chart instance (for external access)
    getChart() {
      return this.chart
    },
    
    // Check if chart is ready
    isReady() {
      return this.chart && !this.isDestroyed
    },
    
    // Create completely non-reactive chart options using Object.create(null)
    createNonReactiveOptions(options) {
      if (!options || typeof options !== 'object') {
        return options
      }
      

      
      try {
        // Use Object.create(null) to create an object with NO prototype chain
        // This completely bypasses Vue's reactivity system
        const isolatedOptions = Object.create(null)
        
        // Copy only the properties that Chart.js needs, avoiding any reactive references
        if (options.responsive !== undefined) isolatedOptions.responsive = options.responsive
        if (options.maintainAspectRatio !== undefined) isolatedOptions.maintainAspectRatio = options.maintainAspectRatio
        if (options.animation !== undefined) {
          isolatedOptions.animation = Object.create(null)
          if (options.animation.duration !== undefined) isolatedOptions.animation.duration = options.animation.duration
          if (options.animation.delay !== undefined) isolatedOptions.animation.delay = options.animation.delay
          if (options.animation.easing !== undefined) isolatedOptions.animation.easing = options.animation.easing
        }
        
        // Handle plugins - create a completely new plugins object with no prototype
        if (options.plugins) {
          isolatedOptions.plugins = Object.create(null)
          
          // Copy legend plugin
          if (options.plugins.legend) {
            const srcLegend = options.plugins.legend
            isolatedOptions.plugins.legend = Object.create(null)
            if (srcLegend.display !== undefined) isolatedOptions.plugins.legend.display = srcLegend.display
            if (srcLegend.position !== undefined) isolatedOptions.plugins.legend.position = srcLegend.position
            if (srcLegend.align !== undefined) isolatedOptions.plugins.legend.align = srcLegend.align
            if (srcLegend.maxHeight !== undefined) isolatedOptions.plugins.legend.maxHeight = srcLegend.maxHeight
            if (srcLegend.maxWidth !== undefined) isolatedOptions.plugins.legend.maxWidth = srcLegend.maxWidth
            if (srcLegend.fullSize !== undefined) isolatedOptions.plugins.legend.fullSize = srcLegend.fullSize
            if (srcLegend.reverse !== undefined) isolatedOptions.plugins.legend.reverse = srcLegend.reverse
            if (typeof srcLegend.onClick === 'function') isolatedOptions.plugins.legend.onClick = srcLegend.onClick
            if (typeof srcLegend.onHover === 'function') isolatedOptions.plugins.legend.onHover = srcLegend.onHover
            if (typeof srcLegend.onLeave === 'function') isolatedOptions.plugins.legend.onLeave = srcLegend.onLeave

            if (srcLegend.labels) {
              const srcLabels = srcLegend.labels
              const dstLabels = Object.create(null)
              if (srcLabels.boxWidth !== undefined) dstLabels.boxWidth = srcLabels.boxWidth
              if (srcLabels.boxHeight !== undefined) dstLabels.boxHeight = srcLabels.boxHeight
              if (srcLabels.color !== undefined) dstLabels.color = srcLabels.color
              if (srcLabels.font) {
                dstLabels.font = Object.create(null)
                if (srcLabels.font.size !== undefined) dstLabels.font.size = srcLabels.font.size
                if (srcLabels.font.family !== undefined) dstLabels.font.family = srcLabels.font.family
                if (srcLabels.font.weight !== undefined) dstLabels.font.weight = srcLabels.font.weight
                if (srcLabels.font.style !== undefined) dstLabels.font.style = srcLabels.font.style
              }
              if (srcLabels.padding !== undefined) dstLabels.padding = srcLabels.padding
              if (typeof srcLabels.generateLabels === 'function') dstLabels.generateLabels = srcLabels.generateLabels
              if (typeof srcLabels.filter === 'function') dstLabels.filter = srcLabels.filter
              if (typeof srcLabels.sort === 'function') dstLabels.sort = srcLabels.sort
              if (srcLabels.pointStyle !== undefined) dstLabels.pointStyle = srcLabels.pointStyle
              if (srcLabels.textAlign !== undefined) dstLabels.textAlign = srcLabels.textAlign
              if (srcLabels.usePointStyle !== undefined) dstLabels.usePointStyle = srcLabels.usePointStyle
              if (srcLabels.pointStyleWidth !== undefined) dstLabels.pointStyleWidth = srcLabels.pointStyleWidth
              if (srcLabels.useBorderRadius !== undefined) dstLabels.useBorderRadius = srcLabels.useBorderRadius
              if (srcLabels.borderRadius !== undefined) dstLabels.borderRadius = srcLabels.borderRadius
              isolatedOptions.plugins.legend.labels = dstLabels
            }
          }
          
          // Copy tooltip plugin
          if (options.plugins.tooltip) {
            isolatedOptions.plugins.tooltip = Object.create(null)
            if (options.plugins.tooltip.enabled !== undefined) isolatedOptions.plugins.tooltip.enabled = options.plugins.tooltip.enabled
            if (options.plugins.tooltip.mode !== undefined) isolatedOptions.plugins.tooltip.mode = options.plugins.tooltip.mode
            if (options.plugins.tooltip.intersect !== undefined) isolatedOptions.plugins.tooltip.intersect = options.plugins.tooltip.intersect
          }
        }
        
        // Handle scales - create completely new scale objects with no prototype
        if (options.scales) {
          isolatedOptions.scales = Object.create(null)
          for (const [scaleKey, srcScale] of Object.entries(options.scales)) {
            const dstScale = Object.create(null)
            if (srcScale.type !== undefined) dstScale.type = srcScale.type
            if (srcScale.display !== undefined) dstScale.display = srcScale.display
            if (srcScale.position !== undefined) dstScale.position = srcScale.position
            if (srcScale.min !== undefined) dstScale.min = srcScale.min
            if (srcScale.max !== undefined) dstScale.max = srcScale.max
            if (srcScale.beginAtZero !== undefined) dstScale.beginAtZero = srcScale.beginAtZero
            if (srcScale.stacked !== undefined) dstScale.stacked = srcScale.stacked
            
            if (srcScale.title) {
              dstScale.title = Object.create(null)
              if (srcScale.title.display !== undefined) dstScale.title.display = srcScale.title.display
              if (srcScale.title.text !== undefined) dstScale.title.text = srcScale.title.text
            }
            
            if (srcScale.grid) {
              dstScale.grid = Object.create(null)
              if (srcScale.grid.display !== undefined) dstScale.grid.display = srcScale.grid.display
              if (srcScale.grid.color !== undefined) dstScale.grid.color = srcScale.grid.color
              if (srcScale.grid.drawOnChartArea !== undefined) dstScale.grid.drawOnChartArea = srcScale.grid.drawOnChartArea
            }
            
            if (srcScale.ticks) {
              dstScale.ticks = Object.create(null)
              if (typeof srcScale.ticks.callback === 'function') dstScale.ticks.callback = srcScale.ticks.callback
              if (srcScale.ticks.stepSize !== undefined) dstScale.ticks.stepSize = srcScale.ticks.stepSize
            }
            
            if (srcScale.time) {
              dstScale.time = Object.create(null)
              if (srcScale.time.unit !== undefined) dstScale.time.unit = srcScale.time.unit
              if (typeof srcScale.time.parser === 'function') dstScale.time.parser = srcScale.time.parser
              if (srcScale.time.displayFormats !== undefined) dstScale.time.displayFormats = srcScale.time.displayFormats
            }
            
            isolatedOptions.scales[scaleKey] = dstScale
          }
        }
        
        // Copy any other top-level properties that might be needed
        if (options.elements !== undefined) isolatedOptions.elements = options.elements
        if (options.layout !== undefined) isolatedOptions.layout = options.layout
        if (options.interaction !== undefined) isolatedOptions.interaction = options.interaction
        

        
        return isolatedOptions
      } catch (error) {
        console.warn('📊 BaseChart: Error creating non-reactive options, using fallback:', error)
        // Fallback: create a minimal non-reactive options object with no prototype
        return Object.assign(Object.create(null), {
          responsive: true,
          maintainAspectRatio: false,
          animation: Object.assign(Object.create(null), { duration: 0 })
        })
      }
    },
    
    // Clear error state to allow chart updates to resume
    clearErrorState() {
      this._errorState = false
      this._lastErrorTime = 0
      console.log('📊 BaseChart: Error state cleared')
    },
    
    // Clear the static chart initialization queue (for emergency cleanup)
    clearChartQueue() {
      this.$options.static.chartInitQueue = []
      this.$options.static.isInitializing = false
      console.log('📊 BaseChart: Chart queue cleared')
    }
  },
  
  async mounted() {
    // Only initialize chart if we have data, otherwise wait for explicit initialization call
    if (this.chartData && this.chartData.datasets && this.chartData.datasets.length > 0) {
      console.log('📊 BaseChart: Data available on mount, initializing chart')
      await this.initializeChart()
    } else {
      console.log('📊 BaseChart: No data on mount, waiting for explicit initialization call')
      // Note: Watchers removed - chart will be initialized via explicit updateChart() calls
    }
  },
  
    // Add beforeUpdate hook to ensure chart cleanup before re-rendering
  beforeUpdate() {
    // If we're about to update and have a chart, ensure it's properly cleaned up
    if (this.chart && !this.isDestroyed) {
      try {
        // Check if chart is still valid
        if (!this.isChartHealthy()) {
          console.log('📊 BaseChart: Chart unhealthy before update, destroying...')
          this.destroyChart()
        }
      } catch (error) {
        console.warn('📊 BaseChart: Error checking chart health before update:', error)
        this.destroyChart()
      }
    }
  },

  beforeUnmount() {
    // Clean up chart when component is unmounted
    this.destroyChart()
  }
}
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.chart-container canvas {
  display: block;
  max-width: 100%;
  height: auto;
}
</style>
